import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@nexus/ui";

import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { MicrosoftIcon } from "@/components/icons/MicrosoftIcon";
import { POPUP_PROVIDER_COPY } from "@/features/auth/lib/popup-sign-in";

import type { PopupProviderId } from "@/features/auth/lib/popup-sign-in";

const PROVIDER_ICONS: Record<PopupProviderId, typeof GoogleIcon> = {
  google: GoogleIcon,
  microsoft: MicrosoftIcon,
};

interface ProviderSignInButtonProps {
  provider: PopupProviderId;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
}

export function ProviderSignInButton({ provider, isLoading, disabled, onClick }: ProviderSignInButtonProps) {
  const { t } = useTranslation();
  const copy = POPUP_PROVIDER_COPY[provider];
  const Icon = PROVIDER_ICONS[provider];

  return (
    <Button
      type="button"
      variant="outline"
      className="min-h-11 w-full gap-2 border-[#dadce0] [@media(hover:hover)_and_(pointer:fine)]:min-h-9 bg-white text-[#3c4043] hover:bg-[#f8f9fa] hover:text-[#3c4043] dark:border-[var(--border)] dark:bg-[var(--card)] dark:text-[var(--foreground)] dark:hover:bg-[var(--accent)]"
      onClick={onClick}
      disabled={disabled}
      aria-label={t(copy.signIn)}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Icon className="h-4 w-4" />
      )}
      <span>{isLoading ? t(copy.opening) : t(copy.signIn)}</span>
    </Button>
  );
}
